import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import {
  getSensors,
  getSensorAlarms,
  getSensorConnectivity,
  getPCOStateChanges,
  countAlertTransitions,
  countAlarmShutoffs,
} from "../unitEventsUtils";
import { computeBuildingTrend } from "../trendUtils";

export type AggregateRow = {
  unitsCount: number | null;
  onlineCount: number | null;
  alertsCount: number | null;
  shutdownsCount: number | null;
  hasPCO: boolean;
  /** Aggregate safety trend across the building's sensors (alarm-based). */
  trendStatus: string | null;
  trendRank: number | null;
  /** Graded signed trend level -3..+3 (positive = worsening). */
  trendLevel: number | null;
  /** 0-100 "most problematic" priority (severity blended with decline slope). */
  priorityScore: number | null;
  priorityBand: string | null;
  /** Highest single-sensor priority in the building, for tie-breaking/secondary display. */
  worstUnitPriority: number | null;
  aggStatus: "loading" | "ready" | "error";
};

export type PortfolioTotals = {
  properties: number;
  units: number;
  onlineSensors: number;
  pcoEquipped: number;
  alerts: number;
  shutdowns: number;
};

export type BuildingAggregatesResult = {
  aggregates: Map<string, AggregateRow>;
  /** Flat list of every alarm record across every sensor, for charting. */
  allAlertRecords: any[];
  totals: PortfolioTotals;
};

type Building = {
  buildingId: string;
  name: string;
  LatLong: [number, number];
};

const ALERT_PAGE_SIZE = 5000;
const REFETCH_INTERVAL = 60_000 * 60; // 1h, matches Sensors view
// Navigations within STALE_TIME reuse the cache (no refetch on remount); GC_TIME
// keeps that cache alive while the user is off the dashboard, so coming back is
// instant rather than a cold reload.
const STALE_TIME = 10 * 60_000; // 10m
const GC_TIME = 2 * 60 * 60_000; // 2h
// 10+ offline sensors saturates the offline weight regardless of property size.
const OFFLINE_COUNT_REF = 10;

/**
 * Fan-out per-building → fetch sensor lists, then per-sensor session totals
 * within the chosen date window, then aggregate into Units + Alerts counts.
 *
 * Returns a `Map<buildingId, AggregateRow>` whose reference is stable across
 * renders unless a query's data actually changed (uses `dataUpdatedAt` +
 * `status` as the memo signature).
 */
export function useBuildingAggregates(
  buildings: Building[],
  startDate: string | null,
  endDate: string | null
): BuildingAggregatesResult {
  // Step 1: sensors per building.
  const sensorQueries = useQueries({
    queries: buildings.map((b) => ({
      queryKey: ["sensors", b.buildingId],
      queryFn: async () => await getSensors(b.buildingId),
      staleTime: STALE_TIME,
      gcTime: GC_TIME,
      refetchOnWindowFocus: false,
      refetchInterval: REFETCH_INTERVAL,
    })),
  });

  // Flatten sensors so we can fan-out one session query per sensor.
  type SensorRef = { buildingId: string; name: string };
  const sensorRefs: SensorRef[] = [];
  buildings.forEach((b, i) => {
    const q = sensorQueries[i];
    const list = q?.data?.data ?? [];
    list.forEach((s: { name: string }) => {
      if (s?.name) sensorRefs.push({ buildingId: b.buildingId, name: s.name });
    });
  });

  // Step 2: alert history per sensor. The alarms_v2 endpoint doesn't accept a
  // date filter, so we cache one fetch per sensor and filter client-side.
  const sessionQueries = useQueries({
    queries: sensorRefs.map((s) => ({
      queryKey: ["alerts-agg", s.name],
      queryFn: async () => await getSensorAlarms(s.name, ALERT_PAGE_SIZE),
      enabled: !!s.name,
      staleTime: STALE_TIME,
      gcTime: GC_TIME,
      refetchOnWindowFocus: false,
      refetchInterval: REFETCH_INTERVAL,
    })),
  });

  // Step 3: PCO state + connectivity per sensor, matching the Sensors page
  // "stove control" logic: a sensor is "controllable" only when it's currently
  // online AND its latest PCO state reports the unit connected. Building-level
  // hasPCO is true when at least one sensor at the building meets that bar.
  const pcoQueries = useQueries({
    queries: sensorRefs.map((s) => ({
      queryKey: ["pco-presence", s.name],
      queryFn: async () => await getPCOStateChanges(s.name, 1),
      enabled: !!s.name,
      staleTime: STALE_TIME,
      gcTime: GC_TIME,
      refetchOnWindowFocus: false,
    })),
  });
  const connectivityQueries = useQueries({
    queries: sensorRefs.map((s) => ({
      queryKey: ["connectivity-presence", s.name],
      queryFn: async () => await getSensorConnectivity(s.name, 1),
      enabled: !!s.name,
      staleTime: STALE_TIME,
      gcTime: GC_TIME,
      refetchOnWindowFocus: false,
    })),
  });

  // Memo signatures: these only change when query data/status actually
  // changes, so the returned Map is stable across no-op renders.
  const sensorSig = sensorQueries
    .map((q) => `${q.status}:${q.dataUpdatedAt ?? 0}`)
    .join("|");
  const sessionSig = sessionQueries
    .map((q) => `${q.status}:${q.dataUpdatedAt ?? 0}`)
    .join("|");
  const pcoSig = pcoQueries
    .map((q) => `${q.status}:${q.dataUpdatedAt ?? 0}`)
    .join("|");
  const connSig = connectivityQueries
    .map((q) => `${q.status}:${q.dataUpdatedAt ?? 0}`)
    .join("|");
  const buildingSig = buildings.map((b) => b.buildingId).join("|");

  return useMemo(() => {
    const result = new Map<string, AggregateRow>();
    const allAlertRecords: any[] = [];
    sessionQueries.forEach((q) => {
      const records = q?.data?.data;
      if (Array.isArray(records)) allAlertRecords.push(...records);
    });

    let onlineSensors = 0;
    let pcoEquipped = 0;
    sensorRefs.forEach((_, idx) => {
      const conn = connectivityQueries[idx]?.data?.data?.[0];
      const pco = pcoQueries[idx]?.data?.data?.[0];
      const online = conn?.eventType === "connected";
      const pcoConnected = pco?.currentUnitConnectedStatus === 1;
      if (online) onlineSensors += 1;
      // PCO presence is known from its last reported state, so count it even when
      // the sensor is offline, since shutdown history is still meaningful.
      if (pcoConnected) pcoEquipped += 1;
    });

    buildings.forEach((b, i) => {
      const sensorsQ = sensorQueries[i];
      if (!sensorsQ) {
        result.set(b.buildingId, {
          unitsCount: null,
          onlineCount: null,
          alertsCount: null,
          shutdownsCount: null,
          hasPCO: false,
          trendStatus: null,
          trendRank: null,
          trendLevel: null,
          priorityScore: null,
          priorityBand: null,
          worstUnitPriority: null,
          aggStatus: "loading",
        });
        return;
      }
      if (sensorsQ.isError) {
        result.set(b.buildingId, {
          unitsCount: null,
          onlineCount: null,
          alertsCount: null,
          shutdownsCount: null,
          hasPCO: false,
          trendStatus: null,
          trendRank: null,
          trendLevel: null,
          priorityScore: null,
          priorityBand: null,
          worstUnitPriority: null,
          aggStatus: "error",
        });
        return;
      }
      if (sensorsQ.isPending) {
        result.set(b.buildingId, {
          unitsCount: null,
          onlineCount: null,
          alertsCount: null,
          shutdownsCount: null,
          hasPCO: false,
          trendStatus: null,
          trendRank: null,
          trendLevel: null,
          priorityScore: null,
          priorityBand: null,
          worstUnitPriority: null,
          aggStatus: "loading",
        });
        return;
      }

      const sensorList: { name: string }[] = sensorsQ.data?.data ?? [];
      const unitsCount = sensorList.length;

      const sessionIdxs: number[] = [];
      sensorRefs.forEach((s, idx) => {
        if (s.buildingId === b.buildingId) sessionIdxs.push(idx);
      });

      // Online sensor count for this building (null until connectivity loads).
      const buildingConnLoadQs = sessionIdxs.map((i2) => connectivityQueries[i2]);
      // Only "loading" before the first result arrives (isPending). A background
      // refetch keeps the cached value visible instead of flashing to loading.
      const connLoading = buildingConnLoadQs.some((q) => q?.isPending);
      const connErrored = buildingConnLoadQs.some((q) => q?.isError);
      const onlineCount =
        connLoading || connErrored
          ? null
          : buildingConnLoadQs.reduce(
              (acc, q) =>
                acc +
                (q?.data?.data?.[0]?.eventType === "connected" ? 1 : 0),
              0
            );

      if (unitsCount === 0) {
        result.set(b.buildingId, {
          unitsCount: 0,
          onlineCount: 0,
          alertsCount: 0,
          shutdownsCount: 0,
          hasPCO: false,
          trendStatus: null,
          trendRank: null,
          trendLevel: 0,
          priorityScore: 0,
          priorityBand: "none",
          worstUnitPriority: 0,
          aggStatus: "ready",
        });
        return;
      }

      const buildingPcoQs = sessionIdxs.map((i2) => pcoQueries[i2]);
      // A property "has a PCO" if any sensor's last reported PCO state shows one
      // connected, regardless of current connectivity, so shutdown counts show
      // (including 0) instead of N/A when PCO sensors are offline.
      const hasPCO = sessionIdxs.some((_, idxInBuilding) => {
        const pco = buildingPcoQs[idxInBuilding]?.data?.data?.[0];
        return pco?.currentUnitConnectedStatus === 1;
      });

      const buildingSessionQs = sessionIdxs.map((i2) => sessionQueries[i2]);
      const anyError = buildingSessionQs.some((q) => q?.isError);
      const anyLoading = buildingSessionQs.some((q) => q?.isPending);

      // Building trend is alarm-based and uses its own 4-week windows, so it's
      // ready as soon as the per-sensor alarm history loads, independent of the
      // date picker (unlike alertsCount/shutdownsCount).
      // Offline weight blends the SHARE of sensors down with the absolute COUNT,
      // so a small property with half its sensors dark ranks up (share), and a
      // handful of offline sensors still registers in a large property (count).
      const offlineSensors =
        onlineCount != null ? Math.max(0, unitsCount - onlineCount) : 0;
      const offlineNorm =
        onlineCount != null && unitsCount > 0
          ? Math.max(
              offlineSensors / unitsCount,
              Math.min(offlineSensors / OFFLINE_COUNT_REF, 1)
            )
          : 0;
      const trend =
        !anyError && !anyLoading
          ? computeBuildingTrend(
              buildingSessionQs.map((q) => q?.data?.data ?? []),
              new Date(),
              { offlineNorm }
            )
          : null;

      if (anyError) {
        result.set(b.buildingId, {
          unitsCount,
          onlineCount,
          alertsCount: null,
          shutdownsCount: null,
          hasPCO,
          trendStatus: null,
          trendRank: null,
          trendLevel: null,
          priorityScore: null,
          priorityBand: null,
          worstUnitPriority: null,
          aggStatus: "error",
        });
        return;
      }
      if (!startDate || !endDate || anyLoading) {
        result.set(b.buildingId, {
          unitsCount,
          onlineCount,
          alertsCount: null,
          shutdownsCount: null,
          hasPCO,
          trendStatus: trend?.status ?? null,
          trendRank: trend?.rank ?? null,
          trendLevel: trend?.level ?? null,
          priorityScore: trend?.priorityScore ?? null,
          priorityBand: trend?.priorityBand ?? null,
          worstUnitPriority: trend?.worstUnitPriority ?? null,
          aggStatus: "loading",
        });
        return;
      }

      let alertsCount = 0;
      let shutdownsCount = 0;
      buildingSessionQs.forEach((q) => {
        const records = q?.data?.data ?? [];
        alertsCount += countAlertTransitions(records, startDate, endDate);
        shutdownsCount += countAlarmShutoffs(records, startDate, endDate);
      });

      result.set(b.buildingId, {
        unitsCount,
        onlineCount,
        alertsCount,
        shutdownsCount,
        hasPCO,
        trendStatus: trend?.status ?? null,
        trendRank: trend?.rank ?? null,
        trendLevel: trend?.level ?? null,
        priorityScore: trend?.priorityScore ?? null,
        priorityBand: trend?.priorityBand ?? null,
        worstUnitPriority: trend?.worstUnitPriority ?? null,
        aggStatus: "ready",
      });
    });

    let totalUnits = 0;
    let totalAlerts = 0;
    let totalShutdowns = 0;
    result.forEach((row) => {
      totalUnits += row.unitsCount ?? 0;
      totalAlerts += row.alertsCount ?? 0;
      totalShutdowns += row.shutdownsCount ?? 0;
    });

    return {
      aggregates: result,
      allAlertRecords,
      totals: {
        properties: buildings.length,
        units: totalUnits,
        onlineSensors,
        pcoEquipped,
        alerts: totalAlerts,
        shutdowns: totalShutdowns,
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingSig, sensorSig, sessionSig, pcoSig, connSig, startDate, endDate]);
}
